import { create } from 'zustand';
import { api } from '@/lib/api/client';
import type { User, MagicLinkResponse, VerifyMagicLinkResponse, AuthMeResponse } from '@/lib/api/types';

/**
 * Auto-provision a Scout profile for this operator.
 * Called silently after login — failure is non-blocking.
 */
async function autoProvisionScout(user: User) {
  try {
    await api.post('/scout/auto-provision', {
      email: user.email,
      userId: user.id,
      name: user.name || undefined,
    });
  } catch {
    // Scout provisioning is non-critical — silently ignore errors
  }
}

interface AuthState {
  user: User | null;
  tenantName: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  magicLinkSent: boolean;

  // Actions
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  resetMagicLinkSent: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenantName: null,
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: false,
  error: null,
  magicLinkSent: false,

  requestMagicLink: async (email: string) => {
    set({ isLoading: true, error: null, magicLinkSent: false });
    try {
      await api.post<MagicLinkResponse>('/auth/magic-link', {
        email: email.toLowerCase(),
      });
      set({ isLoading: false, magicLinkSent: true });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to send magic link',
        isLoading: false,
      });
      throw err;
    }
  },

  verifyMagicLink: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<VerifyMagicLinkResponse>('/auth/magic-link/verify', {
        token,
      });
      api.setAccessToken(response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
      // Auto-provision Scout profile (non-blocking)
      autoProvisionScout(response.user);
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Invalid or expired magic link',
        isLoading: false,
      });
      throw err;
    }
  },

  logout: () => {
    api.setAccessToken(null);
    localStorage.removeItem('refreshToken');
    set({
      user: null,
      tenantName: null,
      isAuthenticated: false,
    });
  },

  checkAuth: async () => {
    if (!api.getAccessToken()) {
      set({ isAuthenticated: false });
      return;
    }
    set({ isLoading: true });
    try {
      const response = await api.get<AuthMeResponse>('/auth/me');
      set({
        user: response.user,
        tenantName: response.tenant.name,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({
        user: null,
        tenantName: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),

  resetMagicLinkSent: () => set({ magicLinkSent: false }),
}));
